import type { ComparisonResult, ModeResult, UsageTotals } from "../types.js";
import {
  formatCost,
  formatDuration,
  formatPercent,
  formatTokens,
} from "../utils/format.js";
import { iconCheck, iconWarning } from "../utils/icons.js";

/**
 * Extract the primary (paid) model's usage from a ModeResult.
 * The primary model is the first entry in byModel — typically GPT-5.4.
 * Subagent models (Qwen3 etc.) run locally for free, so we exclude them
 * from token/cost comparisons to accurately reflect cost savings.
 */
function primaryModelUsage(result: ModeResult): UsageTotals | null {
  if (result.byModel.length > 0) {
    return result.byModel[0].totals;
  }
  return result.totals;
}

export function renderComparison(comparison: ComparisonResult) {
  const el = document.getElementById("comparison")!;
  el.classList.remove("comparison-hidden");

  const { baseline, treatment, timeSaving } = comparison;
  const bt = primaryModelUsage(baseline);
  const tt = primaryModelUsage(treatment);

  // Recompute token/cost savings based on primary model only
  const tokenSaving = bt && tt && bt.totalTokens > 0
    ? (bt.totalTokens - tt.totalTokens) / bt.totalTokens
    : comparison.tokenSaving;
  const costSaving = bt && tt && bt.totalCost > 0
    ? (bt.totalCost - tt.totalCost) / bt.totalCost
    : comparison.costSaving;

  // Label with primary model name
  const primaryLabel = baseline.byModel.length > 0
    ? baseline.byModel[0].model
    : "Tokens";

  el.innerHTML = `
    <div class="comparison-grid">
      ${metricCard(primaryLabel, bt ? formatTokens(bt.totalTokens) : "N/A", tt ? formatTokens(tt.totalTokens) : "N/A", tokenSaving)}
      ${metricCard("Cost", bt ? formatCost(bt.totalCost) : "N/A", tt ? formatCost(tt.totalCost) : "N/A", costSaving)}
      ${metricCard("Time", formatDuration(baseline.durationMs), formatDuration(treatment.durationMs), timeSaving)}
      ${validationCard(baseline, treatment)}
    </div>
    ${modelBreakdown(treatment)}
  `;
}

export function hideComparison() {
  document.getElementById("comparison")!.classList.add("comparison-hidden");
  document.getElementById("comparison")!.innerHTML = "";
}

function metricCard(
  label: string,
  baselineVal: string,
  treatmentVal: string,
  saving: number,
): string {
  const positive = saving > 0;
  const arrow = positive ? "↓" : "↑";
  const sign = positive ? "positive" : "negative";
  return `
    <div class="metric-card metric-card--${sign}">
      <div class="metric-label">${label}</div>
      <div class="metric-values">
        <span class="metric-baseline">${baselineVal}</span>
        <span class="metric-arrow">→</span>
        <span class="metric-treatment">${treatmentVal}</span>
        <span class="metric-change metric-change--${sign}">${arrow} ${formatPercent(saving)}</span>
      </div>
    </div>
  `;
}

function validationCard(baseline: ModeResult, treatment: ModeResult): string {
  const bPassed = baseline.validation.filter((v) => v.ok).length;
  const bTotal = baseline.validation.length;
  const tPassed = treatment.validation.filter((v) => v.ok).length;
  const tTotal = treatment.validation.length;
  const allOk = bPassed === bTotal && tPassed === tTotal;

  return `
    <div class="metric-card metric-card--${allOk ? "positive" : "negative"}">
      <div class="metric-label">Validation</div>
      <div class="metric-values">
        <span class="metric-treatment">${allOk ? `${iconCheck()} All Pass` : `${iconWarning()} Issues`}</span>
      </div>
      <div style="font-size:var(--text-xs); color:var(--text-muted); margin-top:4px;">
        OpenClaw: ${bPassed}/${bTotal} · OmniClaw: ${tPassed}/${tTotal}
      </div>
    </div>
  `;
}

function modelBreakdown(treatment: ModeResult): string {
  if (treatment.byModel.length === 0) {
    return "";
  }
  return `
    <div class="model-breakdown">
      ${treatment.byModel
        .map(
          (m, i) => `
        <div class="model-entry">
          <span class="model-dot ${i === 0 ? "model-dot--primary" : "model-dot--subagent"}"></span>
          <span>${m.provider}/${m.model}</span>
          <span style="color:var(--text-muted)">·</span>
          <span>${m.count} calls</span>
          <span style="color:var(--text-muted)">·</span>
          <span>${formatTokens(m.totals.totalTokens)} tokens</span>
          <span style="color:var(--text-muted)">·</span>
          <span>${formatCost(m.totals.totalCost)}</span>
        </div>
      `,
        )
        .join("")}
    </div>
  `;
}
